/* Atrium Memory API — the self-building knowledge graph.

   Same-origin via Caddy (/atrium/api/memory/*), Bearer-authenticated with the
   Matrix access token. The Go memory service resolves the token to an MXID
   and forwards to the memory-agent (the LLM layer) with the identity stamped
   server-side. Query answers stream as SSE. */

import { matrix } from '@/lib/matrix'
import { AtriumApiError } from '@/lib/atriumApi'

export type MemoryNodeKind = string // person | team | project | concept | decision | event | tool | place | fact | …

export interface MemoryNode {
  id: string
  kind: MemoryNodeKind
  title: string
  summary: string
  aliases: string[]
  /** Human-readable topic — assigned at ingest, consolidated by the reorg pass. */
  category?: string | null
  created_by?: string | null
  created_at: string
  updated_at: string
  degree?: number
}

export interface MemoryNodeFull extends MemoryNode {
  content: string
}

export interface MemoryEdge {
  src: string
  dst: string
  rel: string
  note?: string
}

export interface MemoryEdgeDetail extends MemoryEdge {
  out: boolean
  peer: { id: string; title: string; kind: string }
}

export type DumpStatus = 'pending' | 'processing' | 'done' | 'error'

export interface DumpResult {
  summary: string
  nodes: Array<{ id: string; title: string; kind: string; action: 'created' | 'updated' | 'linked' | 'deleted' }>
}

export interface MemoryFileMeta {
  id: number
  name: string
  mime: string
  size: number
  by_user?: string | null
  created_at?: string
}

export interface MemoryDump {
  id: number
  raw: string
  by_user?: string | null
  status: DumpStatus
  result?: DumpResult | null
  error?: string | null
  follow_links?: boolean
  created_at: string
  files?: MemoryFileMeta[]
  rolled_back?: boolean
}

// ---- the change log: per-dump graph diffs + rollback ---------------------------

/** Full snapshot of a node as it stood before/after one change. */
export interface NodeSnap {
  kind: string
  title: string
  summary: string
  content: string
  aliases: string[]
  created_by?: string | null
  created_at: string
  updated_at: string
}

/** Edge snapshots carry only the note — src/dst/rel live on the change. */
export interface EdgeSnap {
  note: string
}

export interface GraphChange {
  id: number
  source_id: number | null
  entity: 'node' | 'edge'
  action: 'create' | 'update' | 'delete'
  node_id: string | null
  edge_src: string | null
  edge_dst: string | null
  edge_rel: string | null
  before: NodeSnap | EdgeSnap | null
  after: NodeSnap | EdgeSnap | null
  by_user?: string | null
  cause: 'ingest' | 'rollback'
  reverted_at?: string | null
  created_at: string
}

export interface DumpChanges {
  changes: GraphChange[]
  rolled_back: boolean
}

export interface RollbackResult {
  reverted: number
  warnings: string[]
}

/** One plain-language agent action while ingesting a dump (searched, read,
    created, linked, …) — the member-friendly slice of the run trace. */
export interface AgentAction {
  ts: string
  type: 'search' | 'read' | 'fetch' | 'create' | 'update' | 'link' | 'delete' | 'tool'
  detail: string
  node_id?: string
}

export interface MemoryNodeDetail {
  node: MemoryNodeFull
  edges: MemoryEdgeDetail[]
  sources: Array<{
    id: number
    raw: string
    by_user?: string | null
    created_at: string
    files?: MemoryFileMeta[]
  }>
  files?: MemoryFileMeta[]
}

export interface CitedNode {
  id: string
  title: string
  kind: string
}

/** State of the topic-reorganization pass (GET/POST /reorg). */
export interface ReorgState {
  running: boolean
  lastRunAt: string | null
  lastResult: { categories: string[]; assigned: number } | null
  lastError: string | null
}

// ---- agent observability (steward+; enforced by the Go memory service) -------

export type RunStatus = 'running' | 'done' | 'error'

export interface AgentRun {
  id: number
  kind: 'ingest' | 'query'
  source_id?: number | null
  source_raw?: string | null
  question?: string | null
  by_user?: string | null
  provider: string
  model: string
  status: RunStatus
  error?: string | null
  calls: number
  input_tokens: number
  output_tokens: number
  est_cost_usd?: string | number | null
  started_at: string
  finished_at?: string | null
}

export interface AgentCall {
  seq: number
  request: unknown
  text: string
  tool_calls: Array<{ tool: string; input: string }>
  tool_results: Array<{ tool: string; output: string }>
  finish_reason?: string | null
  input_tokens: number
  output_tokens: number
  created_at: string
}

export interface UsageBucket {
  runs: number
  input_tokens: number
  output_tokens: number
  est_cost_usd?: string | number | null
  provider?: string
  model?: string
  kind?: string
  calls?: number
}

export interface AgentUsage {
  overall: UsageBucket
  by_model: UsageBucket[]
  by_kind: UsageBucket[]
}

export interface ActivityEvent {
  ts: number
  type: 'hello' | 'run_start' | 'step' | 'run_end'
  run_id?: number
  kind?: string
  trigger?: string
  model?: string
  seq?: number
  text?: string
  tool_calls?: Array<{ tool: string; input: string }>
  finish_reason?: string
  input_tokens?: number
  output_tokens?: number
  status?: string
  error?: string
  est_cost_usd?: number | null
  recent?: ActivityEvent[]
}

// ---- knowledge export / import (steward+) ------------------------------------

export interface MemoryExport {
  format: 'atrium-memory'
  version: number
  exported_at: string
  nodes: MemoryNodeFull[]
  edges: MemoryEdge[]
}

export type ImportMode = 'merge' | 'replace'

export interface MemoryImportResult {
  nodes_created: number
  nodes_updated: number
  edges: number
  edges_dropped: number
  skipped: string[]
  skipped_count: number
}

function authHeaders(): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  const token = matrix.getSession()?.accessToken
  if (token) headers['Authorization'] = 'Bearer ' + token
  return headers
}

async function api<T>(method: string, path: string, body?: unknown): Promise<T> {
  let res: Response
  try {
    res = await fetch(path, {
      method,
      headers: authHeaders(),
      body: body !== undefined ? JSON.stringify(body) : undefined,
    })
  } catch {
    throw new AtriumApiError('Could not reach the Atrium backend.', 0)
  }
  let data: Record<string, any> = {}
  try {
    data = await res.json()
  } catch {
    /* empty body */
  }
  if (!res.ok) throw new AtriumApiError(data.error || `${method} ${path} failed (${res.status})`, res.status)
  return data as T
}

/** File → base64 without blowing the call stack on big files. */
async function fileToBase64(file: File): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer())
  let binary = ''
  const CHUNK = 0x8000
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK))
  }
  return btoa(binary)
}

/* Uploaded-file bytes need the auth header, so (like Matrix media) they are
   fetched with the token and shown via cached object URLs. */
const fileUrlCache = new Map<number, Promise<string | null>>()

export const memoryApi = {
  dump: (text: string, followLinks = false) =>
    api<MemoryDump>('POST', '/atrium/api/memory/dump', { text, follow_links: followLinks }),

  /** Commit one or more files (with an optional note) to memory. */
  upload: async (files: File[], text = '', followLinks = false) =>
    api<MemoryDump>('POST', '/atrium/api/memory/upload', {
      text,
      follow_links: followLinks,
      files: await Promise.all(
        files.map(async (file) => ({
          name: file.name,
          mime: file.type || 'application/octet-stream',
          data_base64: await fileToBase64(file),
        })),
      ),
    }),

  /** Blob URL for an uploaded file's bytes (token-fetched, cached). */
  fileObjectUrl(id: number): Promise<string | null> {
    let p = fileUrlCache.get(id)
    if (!p) {
      p = fetch(`/atrium/api/memory/files/${id}`, { headers: authHeaders() })
        .then(async (res) => (res.ok ? URL.createObjectURL(await res.blob()) : null))
        .catch(() => null)
      fileUrlCache.set(id, p)
      // Only successes are worth caching — let a failed fetch retry later.
      void p.then((url) => { if (!url) fileUrlCache.delete(id) })
    }
    return p
  },
  dumps: (limit = 20) => api<{ dumps: MemoryDump[] }>('GET', `/atrium/api/memory/dumps?limit=${limit}`),
  dumpStatus: (id: number) => api<MemoryDump>('GET', `/atrium/api/memory/dumps/${id}`),

  /** What the agent did (or is doing) for one dump, in plain terms. */
  dumpActivity: (id: number) => api<{ actions: AgentAction[] }>('GET', `/atrium/api/memory/dumps/${id}/activity`),

  /** The diff one dump made to the graph: every node/edge change with
      before/after snapshots. */
  dumpChanges: (id: number) => api<DumpChanges>('GET', `/atrium/api/memory/dumps/${id}/changes`),

  /** Undo everything a dump did (author or steward; once per dump). */
  rollbackDump: (id: number) => api<RollbackResult>('POST', `/atrium/api/memory/dumps/${id}/rollback`),
  graph: () => api<{ nodes: MemoryNode[]; edges: MemoryEdge[] }>('GET', '/atrium/api/memory/graph'),
  node: (id: string) => api<MemoryNodeDetail>('GET', `/atrium/api/memory/nodes/${encodeURIComponent(id)}`),
  /** Hybrid search. `nodes` = lexical hits then semantic-only; `semantic` lists
      the ids that matched only semantically (ranked below direct matches). */
  search: (q: string) =>
    api<{ nodes: MemoryNode[]; semantic?: string[] }>('GET', `/atrium/api/memory/search?q=${encodeURIComponent(q)}`),

  /** Reorganize topic categories now (steward-gated). Returns the run state. */
  reorg: () => api<ReorgState>('POST', '/atrium/api/memory/reorg'),
  reorgStatus: () => api<ReorgState>('GET', '/atrium/api/memory/reorg'),

  /** Ask the graph. Streams the Markdown answer via onDelta; resolves with the
      nodes the agent actually read (the citations). */
  async query(
    question: string,
    onDelta: (text: string) => void,
    signal?: AbortSignal,
  ): Promise<CitedNode[]> {
    let cited: CitedNode[] = []
    await streamSSE('/atrium/api/memory/query', { question }, signal, (ev) => {
      if (ev.type === 'delta' && typeof ev.text === 'string') onDelta(ev.text)
      else if (ev.type === 'done') cited = ev.nodes || []
      else if (ev.type === 'error') throw new AtriumApiError(ev.error || 'query failed', 500)
    })
    return cited
  },

  // ---- agent observability ----------------------------------------------------

  /** The whole knowledge graph (full content) as a portable document. */
  exportGraph: () => api<MemoryExport>('GET', '/atrium/api/memory/export'),

  /** Restore/merge a previous export. merge upserts by node id; replace wipes
      the graph first. Transactional server-side. */
  importGraph: (data: { nodes: unknown[]; edges: unknown[] }, mode: ImportMode = 'merge') =>
    api<MemoryImportResult>('POST', '/atrium/api/memory/import', {
      format: 'atrium-memory',
      version: 1,
      mode,
      nodes: data.nodes,
      edges: data.edges,
    }),

  runs: (limit = 50, before?: number) =>
    api<{ runs: AgentRun[] }>(
      'GET',
      `/atrium/api/memory/runs?limit=${limit}${before ? `&before=${before}` : ''}`,
    ),
  run: (id: number) => api<{ run: AgentRun; calls: AgentCall[] }>('GET', `/atrium/api/memory/runs/${id}`),
  usage: () => api<AgentUsage>('GET', '/atrium/api/memory/usage'),

  /** Live agent activity (SSE). Resolves when the stream closes; cancel via signal. */
  activityStream(onEvent: (ev: ActivityEvent) => void, signal: AbortSignal): Promise<void> {
    return streamSSE('/atrium/api/memory/activity/stream', undefined, signal, (ev) =>
      onEvent(ev as ActivityEvent),
    )
  },

  // ---- meetings & suggestions (§11) --------------------------------------------

  /** My suggestions (only ever mine — enforced server-side). */
  suggestions: (status?: SuggestionStatus, limit = 100) =>
    api<{ suggestions: Suggestion[] }>(
      'GET',
      `/atrium/api/memory/suggestions?limit=${limit}${status ? `&status=${status}` : ''}`,
    ),

  /** Approve one suggestion. Tasks: create the ticket FIRST (with your own
      token, via atriumApi.createTicket) and pass {board, ticket} here.
      Knowledge: the server queues a dump on the ingestion lane and returns its
      id; pass `payload` when the approver edited the proposal — the edited
      title/content is what gets remembered. */
  approveSuggestion: (
    id: number,
    resolution?: { board: string; ticket: string },
    payload?: { title?: string; content_md?: string },
  ) =>
    api<{ suggestion: Suggestion; dump_id?: number }>(
      'POST',
      `/atrium/api/memory/suggestions/${id}/approve`,
      { ...(resolution ?? {}), ...(payload ? { payload } : {}) },
    ),

  dismissSuggestion: (id: number, reason?: string) =>
    api<{ suggestion: Suggestion }>('POST', `/atrium/api/memory/suggestions/${id}/dismiss`, reason ? { reason } : {}),

  /** One processed meeting (participants only): status, summary, counts. */
  meeting: (id: number) => api<MeetingSummary>('GET', `/atrium/api/memory/meetings/${id}`),

  /** Summaries of MY recorded meetings, newest first (participant-scoped
      server-side). The durable feed: survives buried chat timelines and
      deleted temporary rooms. */
  meetings: (limit = 50) =>
    api<{ meetings: MeetingListItem[] }>('GET', `/atrium/api/memory/meetings?limit=${limit}`),
}

// ---- meetings & suggestions types (§11) ------------------------------------------

export type SuggestionStatus = 'pending' | 'approved' | 'dismissed' | 'expired' | 'superseded'

export interface SuggestionEvidence {
  excerpt: string
  start_ms?: number
  end_ms?: number
  speaker?: string
}

export interface KnowledgeSuggestionPayload {
  title: string
  content_md: string
  speaker: string
  related_nodes?: Array<{ id: string; title?: string }>
}

export interface TaskSuggestionPayload {
  title: string
  description_md: string
  other_parties?: string[]
  due_hint?: string
  unassigned?: boolean
}

export interface Suggestion {
  id: number
  meeting_id: number
  kind: 'knowledge' | 'task'
  target_user: string
  payload: KnowledgeSuggestionPayload | TaskSuggestionPayload
  evidence: SuggestionEvidence | null
  status: SuggestionStatus
  resolution: { dump_id?: number; board?: string; ticket?: string; reason?: string } | null
  resolved_at: string | null
  created_at: string
  room_id?: string
  room_name?: string
  meeting_started_at?: string
}

/** One row of my meeting feed (GET /meetings): the summary and its provenance
    — never the transcript. */
export interface MeetingListItem {
  id: number
  session_id: string
  room_id: string
  room_name: string
  participants: string[]
  started_at: number
  ended_at: number
  partial: boolean
  summary_md: string
  topics: string[]
  processed_at: number | null
}

export interface MeetingSummary {
  id: number
  session_id: string
  room_id: string
  room_name: string
  participants: string[]
  started_at: number
  ended_at: number
  status: 'pending' | 'processing' | 'done' | 'error'
  partial: boolean
  summary_md: string | null
  topics: string[]
  matrix_event_id: string | null
  suggestions: { knowledge: number; tasks: number }
  error: string | null
}

/** Minimal SSE-over-fetch reader shared by query() and activityStream().
    body !== undefined → POST, else GET. Each `data:` frame is JSON. */
async function streamSSE(
  path: string,
  body: unknown | undefined,
  signal: AbortSignal | undefined,
  onEvent: (ev: any) => void,
): Promise<void> {
  let res: Response
  try {
    res = await fetch(path, {
      method: body !== undefined ? 'POST' : 'GET',
      headers: authHeaders(),
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal,
    })
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') return
    throw new AtriumApiError('Could not reach the Atrium backend.', 0)
  }
  if (!res.ok || !res.body) {
    let msg = `${path} failed (${res.status})`
    try {
      msg = (await res.json()).error || msg
    } catch {
      /* not json */
    }
    throw new AtriumApiError(msg, res.status)
  }

  const reader = res.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  try {
    for (;;) {
      const { done, value } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })
      let nl: number
      while ((nl = buffer.indexOf('\n\n')) >= 0) {
        const frame = buffer.slice(0, nl)
        buffer = buffer.slice(nl + 2)
        const line = frame.split('\n').find((l) => l.startsWith('data: '))
        if (!line) continue
        let ev: any
        try {
          ev = JSON.parse(line.slice(6))
        } catch {
          continue
        }
        onEvent(ev)
      }
    }
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') return
    throw e
  }
}
